<template>
  <v-container>
    <v-row>
      <v-col :cols="12" md="10" offset-md="1" lg="8" offset-lg="2">
        <v-toolbar class="mb-2">
          <v-toolbar-title>Scots Connect: Setting Up your Covenant Account</v-toolbar-title>
        </v-toolbar>
        <v-card>
          <v-stepper v-model="step" vertical>
            <v-stepper-step :complete="step > 1" step="1">ReACT: New User Setup</v-stepper-step>
            <v-stepper-content step="1">
              <v-card>
                <v-card-text>
                  <p>The first step in setting up your Covenant account is to get you registered into ReACT, our password reset system. You will be able to use ReACT to reset your password or unlock your account when you need to. Please follow these steps to set up your account within ReACT. Once you are finished setting it up, click Next.</p>
                  <v-row>
                    <v-col :cols="12" md="9">
                      <ol>
                        <li>First, you will need to retrieve your username and default temporary password from your <a href="https://online.covenant.edu/apply/status" target="_blank">Application Portal</a>.</li>
                        <li>Go to <a href="https://apps04.covenant.edu/react" target="_blank">ReACT</a></li> (this link will open in a new tab/window)
                        <li>Enter your Covenant username from your application portal and click Continue (or press Enter)</li>
                        <li>Click on the button for Go to Setup Tool</li>
                        <li>Click on "Authenticate with Covenant Username"</li>
                        <li>Enter your Covenant username, and your default temporary password from your application portal<br/><i>Copy and paste your password to ensure it's accurate</i></li>
                        <li>Follow the prompts to set up challenge questions and answers</li>
                        <li>You can also optionally (but highly recommended) set up an alternate email and phone number to use to authenticate when you need to reset your password</li>
                        <li>Click Finish</li>
                      </ol>
                    </v-col>
                    <v-col :cols="12" md="3">
                      <v-card elevation="4">
                        <v-card-title>Need Help?</v-card-title>
                        <v-card-text>
                          Contect Technology Services<br/>
                          Office Hours: M-F 8am - 4:30pm<br/>
                          706.419.1219<br /><br/>
                          The student must be present on the call. For verification purposes, please have your Student Banner ID from your application portal available.
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-btn color="primary" @click="step++">Done with this step</v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-content>
            <v-stepper-step :complete="step > 2" step="2">ReACT: Password Reset</v-stepper-step>
            <v-stepper-content step="2">
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col :cols="12" md="9">
                      <p>The next step is to reset your password to one you will more easily remember. Please follow these steps to reset your password.</p>
                      <ol>
                        <li>Go to <a href="https://apps04.covenant.edu/react" target="_blank">ReACT</a></li>
                        <li>Enter your Covenant username from your application portal and click Continue (or press Enter)</li>
                        <li>Click on the button for Go to Reset Tool</li>
                        <li>Choose an authentication method. You can use your Covenant username and password like above, or with any of the additional setup options you set up (challenge questions, email, or sms)</li>
                        <li>Once you are authenticated, set up a new password. Pay attention to the password rules listed on the page and make sure you get a green checkmark on all of the requirements.</li>
                        <li>Once ready, click Reset.</li>
                        <li>Ensure that you get a green check mark on the final page for both Active Directory (Covenant username) and Google.</li>
                        <li>Click Finish when done (or close the tab)</li>
                      </ol>
                    </v-col>
                    <v-col :cols="12" md="3">
                      <v-card elevation="4">
                        <v-card-title>Check for Checks</v-card-title>
                        <v-card-text>
                          <p>When you reset your password, make sure you get a green checkmark for each system. This will ensure your password was reset and will by in sync between all of the connected systems.</p>
                          <p>Your password expires every 90 days, so you'll have to reset it every 3 months. You will be notified via email when it's near the time to reset it.</p>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-btn @click="step--">Back</v-btn>
                  <v-btn color="info" @click="step++">Done with this step</v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-content>
            <v-stepper-step :complete="step > 3" step="3">SSO: Securing your Information</v-stepper-step>
            <v-stepper-content step="3">
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col :cols="12" md="9">
                      <p>We use a Microsoft Single-Sign-On (SSO) server to access most of our on-campus systems. Some of these are:</p>
                      <ul class="mb-2">
                        <li>Banner: Student Information System. This is what you will use to register for courses (after your first year), accept/decline Financial Aid, see your grades, and more.</li>
                        <li>Canvas: Learning Management System. This is what you will use to access information related to your courses, and to do some of the placement tests in Scots Connect.</li>
                        <li>Scots Portal: a hub which you can use to access both Banner and Canvas. Also where you can access Great Scots (the campus directory), review your chapel attendance, complete your enrollment verification checklist, submit Study Abroad applications, make schedule or major changes, and more.</li>
                      </ul>
                      <p>Banner, along with some other systems, will require Multi-Factor Authentication (MFA) each time you login. The first time you log into Banner you will be asked to set up your MFA. The preferred MFA method is to use the Microsoft Authenticator app on your smartphone. We recommend installing the app now so it is ready when you need it (you do not need to do any setup in the app now; you will be prompted to set it up when it's needed). If you are using your smartphone now, you can use a link below to install the app, or you can search for Microsoft Authenticator in your app store. If you do not have a smartphone, please email <a href="mailto:techsupport@covenant.edu" target="_blank">techsupport@covenant.edu</a> who can help you find an alternative solution.</p>
                      <p><a href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458" target="_blank">Microsoft Authenticator for iOS</a></p>
                      <p><a href="https://play.google.com/store/apps/details?id=com.azure.authenticator&hl=en_US&gl=US&pli=1https://mail.google.com/" target="_blank">Microsoft Authenticator for Android</a></p>
                    </v-col>
                    <v-col :cols="12" md="3">
                      <v-card elevation="4">
                        <v-card-title>Important Note</v-card-title>
                        <v-card-text>
                          <p>The Microsoft and Google logins ask for your email address, not just your username. Your email address is typically (but not always) the same as your username but with @covenant.edu on the end, but refer to your <a href="https://online.covenant.edu/apply/status" target="_blank">application portal</a> for your actual email address.</p>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-btn @click="step--" class="mr-2">Back</v-btn>
                  <v-btn color="info" @click="step++">Done with this step</v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-content>
            <v-stepper-step step="4">Gmail: Covenant Email</v-stepper-step>
            <v-stepper-content step="4">
              <v-card>
                <v-card-text>
                  <v-row>
                    <v-col :cols="12" md="9">
                      <p>Covenant uses Gmail for your Covenant email. Most campus communications will be sent to your Covenant email account, so we recommend getting it set up on your phone, or at least checking it regularly. The password for your email account should have been reset during step 2 above, and so should be the same password. You can get your Covenant email address from your <a href="https://online.covenant.edu/apply/status" target="_blank">application portal</a>. This is what you will enter into Google to log into Gmail.</p>
                      <p><a href="https://mail.google.com/" target="_blank">Log into Gmail</a></p>
                      <h4>MFA</h4>
                      <p>We are now requiring MFA on all Google accounts. You will not be prompted to set it up automatically by Google, but you will need to set this up within 14 days of when your account was set up. Please set this up as early as you can, following <a href="https://support.google.com/accounts/answer/10956730?hl=en%5C" target="_blank">these instructions</a> for setting up Google MFA on your smartphone. If you don't have a smartphone, then you can use the same security key for Google as you can use with Microsoft. Please email techsupport@covenant.edu or call 706.419.1219 for assistance.</p>
                    </v-col>
                    <v-col :cols="12" md="3">
                      <v-card elevation="4">
                        <v-card-title>Phone Setup</v-card-title>
                        <v-card-text>
                          <p>We recommend that you set up your email on your phone. Email is the default way that staff offices and most faculty will communicate with you, so it's important to keep it handy and check it often.</p>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-actions>
                  <v-btn @click="step--" class="mr-2">Back</v-btn>
                  <v-dialog v-model="finishDialog" width="600">
                    <template v-slot:activator="{ on }">
                      <v-btn v-on="on" color="info">Finish</v-btn>
                    </template>
                    <v-card>
                      <v-card-title>All Done!</v-card-title>
                      <v-card-text>
                        <p>Congratulations, you have just finished setting up your Covenant account. You are now ready to log into Scots Connect and begin learning about resources and information that will be helpful to you as you being your journey as a Covenant Scot.</p>
                        <p>When you click the button below you will be directed back to the Scots Connect login page where you can now log in with your assigned username and the password you have set.</p>
                      </v-card-text>
                      <v-card-actions>
                        <v-btn to="/" color="info">Proceed to Login</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                  <v-spacer></v-spacer>
                  <v-btn outlined to="/">Cancel</v-btn>
                </v-card-actions>
              </v-card>
            </v-stepper-content>
          </v-stepper>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { ref } from '@vue/composition-api'

export default {
  setup (props) {
    const step = ref(1)
    const finishDialog = ref(false)

    return {
      step,
      finishDialog
    }
  }
}
</script>
